import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, LinearProgress, Chip, useTheme } from '@mui/material';
import { Schedule, CheckCircle } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetProjects } from '../redux/sagas/requests/project';
import { statusColor } from '../utils/statusColors';
import { formatMonthDay } from '../utils/dateUtils';


const ProjectList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const ProjectCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    '&:last-child': {
        marginBottom: 0,
    },
}));

const ProjectHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
});

const ProjectName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
        color: theme.palette.primary.dark,
    },
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => ({
    height: 22,
    fontSize: theme.typography.size.xs,
    backgroundColor: statuscolor,
    color: '#fff',
}));

const ProgressSection = styled(Box)({
    marginBottom: 8,
});

const ProgressHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
});

const ProgressLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const ProgressValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
}));

const StyledProgress = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.palette.surface.track,
}));

const MetricsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
}));

const MetricItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const ProjectProgressWidget = ({ widgetId, config = {}, onRemove }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { data: projects, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetProjects(0, 100),
        cacheKey: 'projects-active-progress',
        transform: (response) => {
            const projectList = response;
            return projectList
        },
    });

    const calculateProgress = (project) => {
        if (project.progress !== undefined) return project.progress;
        if (project.completedTasks && project.totalTasks) {
            return Math.round((project.completedTasks / project.totalTasks) * 100);
        }
        return 0;
    };

    const formatDate = (date) => formatMonthDay(date) || 'No deadline';

    const hasData = (projects || []).length > 0;

    return (
        <BaseWidget
            title="Project Progress"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <Typography variant="body2">
                        No active projects
                    </Typography>
                </EmptyState>
            ) : (
                <ProjectList>
                    {(projects || []).map((project) => {
                        const progress = calculateProgress(project);
                        return (
                            <ProjectCard key={project._id}>
                                <ProjectHeader>
                                    <ProjectName onClick={() => navigate(`/account/${project.account.slug}/project/${project._id}`)}>{project.name}</ProjectName>
                                    <StatusChip
                                        label={project.status}
                                        size="small"
                                        statuscolor={statusColor(theme, 'project', project.status)}
                                    />
                                </ProjectHeader>

                                <ProgressSection>
                                    <ProgressHeader>
                                        <ProgressLabel>Progress</ProgressLabel>
                                        <ProgressValue>{progress}%</ProgressValue>
                                    </ProgressHeader>
                                    <StyledProgress
                                        variant="determinate"
                                        value={progress}
                                        color={progress >= 90 ? 'success' : progress >= 50 ? 'primary' : 'warning'}
                                    />
                                </ProgressSection>

                                <MetricsRow>
                                    <MetricItem>
                                        <Schedule fontSize="small" />
                                        {formatDate(project.dueDate)}
                                    </MetricItem>
                                    {project.totalTasks > 0 && (
                                        <MetricItem>
                                            <CheckCircle fontSize="small" />
                                            {project.completedTasks || 0}/{project.totalTasks} tasks
                                        </MetricItem>
                                    )}
                                </MetricsRow>
                            </ProjectCard>
                        );
                    })}
                </ProjectList>
            )}
        </BaseWidget>
    );
};

export default ProjectProgressWidget;
